import { drawing as csDrawing } from '@cornerstonejs/tools';
import type { Types as CornerstoneTypes } from '@cornerstonejs/core';

/** The VTI trace's redraw-preview dash. */
export const EDIT_PREVIEW_DASH = '4,3';

type Point2 = CornerstoneTypes.Point2;

interface PreviewAnnotation {
  annotationUID: string;
}

export type RenderContour = (
  enabledElement: { viewport: { id: string } },
  svgDrawingHelper: unknown,
  annotation: PreviewAnnotation
) => void;

export interface ContourEditPreviewTool {
  toolGroupId: string;
  editData?: {
    editCanvasPoints?: Point2[];
    stagedDragPoints?: Point2[];
  } | null;
  drawData?: {
    canvasPoints: Point2[];
    /** Set when an existing open trace is extended from its end. */
    extensionFrom?: number;
    stagedDragPoints?: Point2[];
  } | null;
  getToolName: () => string;
  renderContour: RenderContour;
  getAnnotationStyle: (context: {
    annotation: PreviewAnnotation;
    styleSpecifier: Record<string, string>;
  }) => { color?: string; lineWidth?: number | string };
}

/**
 * Reshaping an area / volume works like the VTI trace: the contour stays as it
 * is and the drag path is drawn dotted on top; the edit applies on release.
 */
export function renderContourEditPreview(
  tool: ContourEditPreviewTool,
  enabledElement: { viewport: { id: string } },
  svgDrawingHelper: unknown,
  annotation: PreviewAnnotation
): void {
  renderWithDottedPath(tool, enabledElement, svgDrawingHelper, annotation, [
    ...(tool.editData?.stagedDragPoints ?? []),
    ...(tool.editData?.editCanvasPoints ?? []),
  ]);
}

/** Extending an open trace from its end: the extension is the dotted path. */
export function renderEndEditPreview(
  tool: ContourEditPreviewTool,
  enabledElement: { viewport: { id: string } },
  svgDrawingHelper: unknown,
  annotation: PreviewAnnotation
): void {
  const {
    canvasPoints = [],
    extensionFrom = 0,
    stagedDragPoints = [],
  } = tool.drawData ?? {};
  renderWithDottedPath(tool, enabledElement, svgDrawingHelper, annotation, [
    ...stagedDragPoints,
    ...canvasPoints.slice(extensionFrom),
  ]);
}

function renderWithDottedPath(
  tool: ContourEditPreviewTool,
  enabledElement: { viewport: { id: string } },
  svgDrawingHelper: unknown,
  annotation: PreviewAnnotation,
  points: Point2[]
): void {
  tool.renderContour(enabledElement, svgDrawingHelper, annotation);
  if (points.length < 2) return;
  const { color, lineWidth } = tool.getAnnotationStyle({
    annotation,
    styleSpecifier: {
      toolGroupId: tool.toolGroupId,
      toolName: tool.getToolName(),
      viewportId: enabledElement.viewport.id,
      annotationUID: annotation.annotationUID,
    },
  });
  csDrawing.drawPolyline(
    svgDrawingHelper as never,
    annotation.annotationUID,
    'edit-preview',
    points,
    {
      color,
      width: Number(lineWidth),
      closePath: false,
      lineDash: EDIT_PREVIEW_DASH,
    }
  );
}
